// The one fetch wrapper, with the error semantics upstream's `http.error.interceptor.ng1`
// gave every call (plan §2.2).
//
// The interceptor is not decoration: 401 and 403 mean different things in this app (session
// gone vs. permission denied), and the SPA routes on that difference. Losing it would turn
// an expired session into a generic failure dialog.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly url: string,
    readonly body: unknown,
  ) {
    super(`${url} failed with ${String(status)}`)
    this.name = 'ApiError'
  }

  /** Session gone or never established — upstream sends these back to the auth flow. */
  get isUnauthenticated(): boolean {
    return this.status === 401
  }

  /** Authenticated but not allowed. NOT the same as 401, and must not be treated as it. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  /**
   * MFA is enrolled and this session has not validated it. The server signals it through the
   * rejection BODY rather than the status (§1.3's otherwise handler reads `mfaValidated`),
   * so it has to survive error construction.
   */
  get needsMfa(): boolean {
    return (
      typeof this.body === 'object' &&
      this.body !== null &&
      'mfaValidated' in this.body &&
      (this.body as { mfaValidated?: unknown }).mfaValidated === false
    )
  }
}

/**
 * Where the API lives, for callers that are NOT same-origin with it.
 *
 * The SPA leaves this empty and must: it is served BY the proxy, so a bare path is the whole
 * point (§1.4, §3). The shell is the other case — its pages run on `chrome-extension://…`,
 * where a bare `/chrome-extension-api/user` resolves against the EXTENSION's own origin and
 * reaches nothing. It gets the tenant from `chrome.storage.local`'s `API_URL` and sets it
 * here. The session still travels: the tenant's cookies are host-only to the tenant, so a
 * credentialed cross-origin request from the extension carries them, which is what
 * `host_permissions` is for.
 */
let baseUrl = ''

export function configureApi(options: { baseUrl: string }): void {
  // No trailing slash: every path in this package starts with one.
  baseUrl = options.baseUrl.replace(/\/$/, '')
}

export function apiUrl(path: string): string {
  return `${baseUrl}${path}`
}

/**
 * What upstream's `HttpErrorInterceptor` (`http.error.interceptor.ng1.ts`) reports through
 * `alerts.error(message)`: the server's own `error.message` when it sent one, else nothing, and the
 * caller shows the generic "network error" text.
 */
export interface RequestFailure {
  status: number
  url: string
  message?: string
}

/**
 * ⚠️ ONE LISTENER, AND ONLY THE SPA REGISTERS IT. Upstream's interceptor pops an alert for every
 * failed call on every screen, which is a page-level UI concern this package has no business
 * drawing. The SPA mounts the dialog and registers here; the popup does not, so its requests stay
 * silent, as upstream's popup (a different app, with no interceptor) is.
 */
let failureListener: ((failure: RequestFailure) => void) | undefined

export function onRequestFailure(listener: ((failure: RequestFailure) => void) | undefined): void {
  failureListener = listener
}

/**
 * A response whose headers arrived and whose BODY could not be used: the read was aborted (the
 * caller's timer fired after the headers), or a 2xx carried something that is not JSON — a
 * proxy's page, a captive portal, a session wall. Upstream's `fetch`-based probes throw a
 * `FetchError` carrying the `Response` in both cases (whitelabel-ext `29621a2`, `4853064`), so
 * the caller's log can say which status and content-type it came with. Only a request made with
 * `requireJson` raises it.
 */
export class ResponseBodyError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly url: string,
    readonly contentType: string | null,
    /** The read was aborted, not answered with something unparseable. */
    readonly aborted: boolean,
  ) {
    super(message)
    this.name = 'ResponseBodyError'
  }
}

export interface RequestOptions {
  /**
   * `config.suppressExceptions` — the caller handles the failure itself and the generic alert would
   * be wrong. Upstream sets it in exactly three places this port has: the SSO-required check, MFA
   * validation and the add-to-job panel.
   */
  suppressErrorDialog?: boolean
  /**
   * `res.json()` semantics for a 2xx: a body that is not JSON REJECTS (as a `ResponseBodyError`)
   * instead of coming back as text. Off by default, because some endpoints answer a bare string
   * (see `safeJson`). Also turns an abort DURING the body read into a `ResponseBodyError`, which
   * keeps the status and content-type an `AbortError` would drop.
   */
  requireJson?: boolean
}

/** `responseErrorHandler`'s rule: 4xx and 5xx except 403, which is "not allowed", not a failure. */
function reportsStatus(status: number): boolean {
  return status >= 400 && status !== 403
}

function serverMessage(body: unknown): string | undefined {
  const error = (body as { error?: { message?: unknown } } | null)?.error
  return typeof error?.message === 'string' && error.message.length > 0 ? error.message : undefined
}

export async function request<T>(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  init: RequestInit = {},
  options: RequestOptions = {},
): Promise<T> {
  const url = apiUrl(path)
  const report = (failure: RequestFailure): void => {
    if (options.suppressErrorDialog !== true) failureListener?.(failure)
  }
  let response: Response
  try {
    response = await fetch(url, {
      method,
      // Same-origin through the proxy, carrying the tenant's cookies (§1.4). There is no
      // token to add and no Authorization header to set: if this ever needs one, the proxy
      // design has been abandoned.
      credentials: 'include',
      headers: {
        accept: 'application/json',
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
        ...init.headers,
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...init,
    })
  } catch (error) {
    // Status 0 upstream: the request never got an answer. A CANCELLED one is not a failure
    // (`isRequestCanceled`) — here that is an `AbortError`, which every timeout and superseded
    // search in this package raises on purpose.
    if (!(error instanceof DOMException && error.name === 'AbortError')) report({ status: 0, url })
    throw error
  }

  let text: string
  try {
    // ⚠️ STILL INSIDE THE CALLER'S SIGNAL. `fetch` settles when the HEADERS arrive, so a timer
    // that bounds a request must stay armed through this read — a server that sends headers and
    // then stalls the body would otherwise hang with no bound at all (whitelabel-ext `22f31b9`).
    // Every bounded caller here clears its timer only after `request` settles.
    text = await response.text()
  } catch (error) {
    if (options.requireJson !== true) throw error
    const aborted = error instanceof DOMException && error.name === 'AbortError'
    throw new ResponseBodyError(
      aborted
        ? `${url} timed out while reading the body`
        : `${url} failed while reading the body: ${error instanceof Error ? error.message : String(error)}`,
      response.status,
      url,
      response.headers.get('content-type'),
      aborted,
    )
  }
  const parsed: unknown = text.length > 0 ? safeJson(text) : null

  if (!response.ok) {
    if (reportsStatus(response.status)) report({ status: response.status, url, ...messageOf(parsed) })
    throw new ApiError(response.status, url, parsed)
  }
  if (options.requireJson === true) {
    const failure = jsonFailure(text)
    if (failure !== undefined) {
      throw new ResponseBodyError(
        `${url} returned a non-JSON body: ${failure}`,
        response.status,
        url,
        response.headers.get('content-type'),
        false,
      )
    }
  }
  // `responseHandler`: a 2xx whose body carries an `error` object is a failure too, reported
  // unless the server marked it `intercept: false`, and rejected either way.
  const bodyError = (parsed as { error?: { intercept?: unknown } } | null)?.error
  if (typeof parsed === 'object' && parsed !== null && typeof bodyError === 'object' && bodyError !== null) {
    if (bodyError.intercept !== false) report({ status: response.status, url, ...messageOf(parsed) })
    throw new ApiError(response.status, url, parsed)
  }
  return parsed as T
}

function messageOf(body: unknown): { message?: string } {
  const message = serverMessage(body)
  return message === undefined ? {} : { message }
}

/** `res.json()`'s verdict on `text`: undefined when it parses, else the parser's message. */
function jsonFailure(text: string): string | undefined {
  try {
    JSON.parse(text)
    return undefined
  } catch (error) {
    return error instanceof Error ? error.message : String(error)
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    // Some endpoints answer with a bare string (the MFA QR image is base64 text), so a
    // parse failure is data, not an error.
    return text
  }
}
